import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import DialogBox from '../core-components/theme/components/DialogBox/DialogBox';
import { AutocompleteInput } from '../core-components/theme/components/Fields';
import { Button, ButtonRow } from '../core-components/theme/components/Buttons';
import { InlineAlert } from '../core-components/theme/components/InlineAlert';
import { viewablePeopleQueryOptions } from '../session';
import type { ViewablePerson } from '../session';

import { useStartViewAs } from './headerComponents/useViewAsSwitch';
import type { StartRefusal } from './headerComponents/useViewAsSwitch';

interface ViewAsProps {
  readonly open: boolean;
  readonly handleClose: () => void;
}

const VIEW_AS_DIALOG_WIDTH = 440;

const NO_PEOPLE: readonly ViewablePerson[] = [];

const personKey = (person: ViewablePerson): string => person.id;

const personName = (person: ViewablePerson): string => person.name;

const PersonOption = ({ person }: { readonly person: ViewablePerson }) => (
  <Stack component="span">
    <span>{person.name}</span>
    {person.email !== null && (
      <Typography component="span" variant="caption1" color="text.secondary">
        {person.email}
      </Typography>
    )}
  </Stack>
);

const refusalText = (t: TFunction, refusal: StartRefusal): string => {
  switch (refusal) {
    case 'notAllowed':
      return t('viewAs.refused.notAllowed', 'You may not view the app as this person.');
    case 'targetUnavailable':
      return t('viewAs.refused.targetUnavailable', 'This person’s account isn’t active.');
    case 'alreadyViewing':
      return t('viewAs.refused.alreadyViewing', 'Stop viewing as the current person first.');
    case 'self':
      return t('viewAs.refused.self', 'You can’t view the app as yourself.');
    case 'failed':
      return t('viewAs.refused.failed', 'Couldn’t start View As. Check your connection and try again.');
    default: {
      const unhandled: never = refusal;
      throw new Error(`View As refusal without words: ${JSON.stringify(unhandled)}`);
    }
  }
};

function ViewAs({ open, handleClose }: ViewAsProps) {
  const { t } = useTranslation();
  const [listOpen, setListOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<ViewablePerson | null>(null);
  const people = useQuery({ ...viewablePeopleQueryOptions(search), enabled: open && listOpen, placeholderData: keepPreviousData });
  const { start, starting, refusal } = useStartViewAs();

  const noOptionsText = people.isError
    ? t('viewAs.people.error', 'Couldn’t load people. Close the list and open it again to retry.')
    : t('viewAs.people.none', 'No one matches');

  return (
    <DialogBox
      open={open}
      title={t('viewAs.dialog.title', 'View As')}
      handleClose={handleClose}
      width={VIEW_AS_DIALOG_WIDTH}
      stopPropagation={false}
      footer={
        <ButtonRow align="end">
          <Button intent="secondary" onClick={handleClose} disabled={starting}>
            {t('viewAs.button.cancel', 'CANCEL')}
          </Button>
          <Button intent="primary" onClick={() => person !== null && start(person)} disabled={person === null} loading={starting}>
            {t('viewAs.button.submit', 'SUBMIT')}
          </Button>
        </ButtonRow>
      }
    >
      <Stack gap={3}>
        <AutocompleteInput<ViewablePerson>
          label={t('viewAs.field.selectUser', 'Select User')}
          value={person}
          onChange={setPerson}
          options={people.data ?? NO_PEOPLE}
          getOptionKey={personKey}
          getOptionLabel={personName}
          renderOption={(option) => <PersonOption person={option} />}
          onSearch={setSearch}
          onOpen={() => setListOpen(true)}
          onClose={() => setListOpen(false)}
          loading={listOpen && people.isFetching}
          loadingText={t('viewAs.people.searching', 'Searching people…')}
          noOptionsText={noOptionsText}
          placeholder={t('viewAs.people.placeholder', 'Search by name')}
          disabled={starting}
        />
        {refusal !== null && <InlineAlert severity="error" role="alert" message={refusalText(t, refusal)} />}
      </Stack>
    </DialogBox>
  );
}

export default ViewAs;
